<script setup>
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination, Autoplay } from "swiper/modules";

// CSS ضروري
import "swiper/css";
import "swiper/css/pagination";
</script>

<template>
  <div class="col-xl-4" v-if="categories.length">
    <div class="card card-xl-stretch mb-5 mb-xl-8">
      <!-- Header -->
      <div class="card-header align-items-center border-0 mt-3">
        <h3 class="card-title align-items-start flex-column">
          <span class="fw-bold text-dark fs-3">
            <i
              class="fa fa-list"
              style="
                border-radius: 10px;
                padding: 8px 10px;
                background: #f4dc1763;
                color: #ff9d00;
              "
            ></i>
            {{ $t("home.qbank_card.title") }}
          </span>
          <span class="text-gray-400 mt-2 fw-semibold fs-6">
            {{ $t("home.qbank_card.subtitle") }}
          </span>
        </h3>
      </div>

      <!-- Body -->
      <div class="card-body pt-5">
        <div v-if="isLoadingQbanks" class="text-center mt-3">
          <span class="spinner-border spinner-border-lg text-success"></span>
          <p>{{ $t("home.qbank_card.loading") }}</p>
        </div>

        <div v-else>
          <Swiper
            :modules="[Pagination, Autoplay]"
            :slides-per-view="1"
            :space-between="10"
            :pagination="{ clickable: true }"
            :autoplay="{ delay: 2500, disableOnInteraction: false }"
            loop
            class="mySwiper"
          >
            <SwiperSlide v-for="(cat, index) in categories" :key="index">
              <div class="d-flex mb-7">
                <div class="d-flex flex-column flex-grow-1 pe-3">
                  <span class="fs-5 text-gray-800 fw-bold">
                    {{ cat.name }}
                  </span>
                  <span class="text-gray-400 fw-semibold fs-7">
                    {{ $t("home.qbank_card.total_points") }}
                    <span class="text-success fw-bold">
                      {{ cat.points }}
                    </span>
                  </span>
                  <span class="text-gray-400 fw-semibold fs-7">
                    {{ $t("home.qbank_card.rank") }}
                    <span class="text-success fw-bold">
                      #{{ cat.user_rank }}
                    </span>
                  </span>
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";

export default {
  name: "DashboardView",
  data() {
    return {
      token: localStorage.getItem("access_token"),
      isLoadingQbanks: false,
      categories: [], // هنا نخزن sub_category + child_categories
    };
  },
  mounted() {
    this.getQbankLatest();
  },
  methods: {
    async getQbankLatest() {
      this.isLoadingQbanks = true;
      try {
        const response = await fetch(`${apiUrl}/qbanks/latest`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();

          // توحيد sub_category + child_categories
          let merged = [];
          if (data.sub_category) merged.push(data.sub_category);
          if (data.child_categories && data.child_categories.length) {
            merged = merged.concat(data.child_categories);
          }

          this.categories = merged;
        } else {
          console.error("فشل في جلب بيانات بنك الأسئلة");
        }
      } catch (error) {
        console.error("خطأ في جلب بيانات بنك الأسئلة:", error);
      } finally {
        this.isLoadingQbanks = false;
      }
    },
  },
};
</script>

<style scoped>
/* ========== Animations ========== */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* ========== Card ========== */
.card {
  border-radius: 20px !important;
  border: 1px solid rgba(255, 193, 7, 0.15) !important;
  background: linear-gradient(145deg, #ffffff 0%, #fffef8 100%);
  box-shadow: 0 8px 32px rgba(255, 193, 7, 0.08);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  animation: fadeInUp 0.5s ease forwards;
  position: relative;
}
.card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #ffc107, #ffb300, #ffc107);
  background-size: 200% 100%;
  animation: shimmer 3s linear infinite;
}
.card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 50px rgba(255, 193, 7, 0.15);
  border-color: rgba(255, 193, 7, 0.25) !important;
}

/* ========== Card Header ========== */
.card-header {
  background: transparent;
  border-bottom: 1px solid rgba(255, 193, 7, 0.1);
  padding: 20px 24px;
}

.card-title .fw-bold {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.1rem;
}
.card-title .fw-bold i {
  width: 42px;
  height: 42px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 12px !important;
  font-size: 1rem;
  background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%) !important;
  color: #ff9800 !important;
  box-shadow: 0 4px 15px rgba(255, 152, 0, 0.2);
  transition: all 0.3s ease;
}
.card:hover .card-title .fw-bold i {
  transform: scale(1.1) rotate(5deg);
}

/* ========== Card Body ========== */
.card-body {
  text-align: right;
  padding: 20px 24px;
}

/* ========== Category Title ========== */
.card-body .fs-5.fw-bold {
  font-weight: 700 !important;
  color: #1a1a2e;
  transition: color 0.3s ease;
  line-height: 1.5;
}
.card-body .fs-5.fw-bold:hover {
  color: #ff9800;
}

/* ========== Stats ========== */
.text-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.1rem !important;
}

/* ========== Spinner ========== */
.spinner-border {
  color: #ff9800 !important;
}

/* ========== Swiper ========== */
.mySwiper {
  padding-bottom: 30px;
}

:deep(.swiper-pagination-bullet) {
  width: 10px;
  height: 10px;
  background: rgba(255, 193, 7, 0.3);
  opacity: 1;
  transition: all 0.3s ease;
}

:deep(.swiper-pagination-bullet-active) {
  background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
  width: 24px;
  border-radius: 5px;
}

/* ========== DARK MODE ========== */
[data-bs-theme="dark"] .card,
[data-theme="dark"] .card,
.dark .card {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(255, 193, 7, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
[data-bs-theme="dark"] .card:hover,
[data-theme="dark"] .card:hover,
.dark .card:hover {
  box-shadow: 0 20px 50px rgba(255, 193, 7, 0.15);
}

[data-bs-theme="dark"] .card-header,
[data-theme="dark"] .card-header,
.dark .card-header {
  border-color: rgba(255, 193, 7, 0.15);
}

[data-bs-theme="dark"] .card-title .fw-bold,
[data-bs-theme="dark"] .card-title .text-dark,
[data-theme="dark"] .card-title .fw-bold,
[data-theme="dark"] .card-title .text-dark,
.dark .card-title .fw-bold,
.dark .card-title .text-dark {
  color: #e4e6eb !important;
}

[data-bs-theme="dark"] .text-gray-400,
[data-theme="dark"] .text-gray-400,
.dark .text-gray-400 {
  color: #b0b3b8 !important;
}

[data-bs-theme="dark"] .card-body .fs-5.fw-bold,
[data-bs-theme="dark"] .text-gray-800,
[data-theme="dark"] .card-body .fs-5.fw-bold,
[data-theme="dark"] .text-gray-800,
.dark .card-body .fs-5.fw-bold,
.dark .text-gray-800 {
  color: #e4e6eb !important;
}
[data-bs-theme="dark"] .card-body .fs-5.fw-bold:hover,
[data-theme="dark"] .card-body .fs-5.fw-bold:hover,
.dark .card-body .fs-5.fw-bold:hover {
  color: #ffc107 !important;
}

[data-bs-theme="dark"] .text-success,
[data-theme="dark"] .text-success,
.dark .text-success {
  background: linear-gradient(135deg, #00e050 0%, #8bffb3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

[data-bs-theme="dark"] :deep(.swiper-pagination-bullet),
[data-theme="dark"] :deep(.swiper-pagination-bullet),
.dark :deep(.swiper-pagination-bullet) {
  background: rgba(255, 193, 7, 0.4);
}
</style>
